import Vue from 'vue';
import axios from "../axios";
import { DateTime } from "luxon";
import Text from "@/inside/models/Text/Text";
import Textbase from "@/inside/models/Textbase/Textbase";
import Assignment from "@/inside/models/Assignment/Assignment";
import Assessment from "@/inside/models/Assessment/Assessment";

export default {
    actions: {
        /**
         * Fetches assignments for given site.
         *
         * @param {function} commit
         * @param {Array} rootGetters
         * @param {Textbase} textbase
         * @return {Promise}
         */
        fetch({commit, rootGetters}, textbase) {
            if (!(textbase instanceof Textbase)) {
                throw new TypeError(`VueX 'assignments/fetch' action expects payload to be an instance of Textbase, ${typeof textbase} given`);
            }

            return new Promise((resolve, reject) => {
                axios.get(`/textbases/${textbase.id}/assignments`, {
                    validateStatus: status => status === 200
                }).then((response) => {
                    response.data.forEach((assignmentData) => {
                        const text = rootGetters["texts/one"](assignmentData.text.id);

                        if (text instanceof Text) {
                            const assignment = Assignment.fromAPI(assignmentData);
                            assignment.textbase = textbase;
                            assignment.text = text;
                            commit("set", assignment);
                        }
                    });

                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Loads assignments for all sites.
         *
         * @param {function} dispatch
         * @param {object} rootGetters
         * @return {Promise}
         */
        init({dispatch, rootGetters}) {
            return new Promise((resolve, reject) => {
                Promise.all(rootGetters["permissions/allTextbases"].map((textbase) => {
                    return dispatch("fetch", textbase);
                })).then((responses) => {
                    resolve(responses);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Triggers given `assignment` re-assessment through Echoline API.
         * @param {function} dispatch
         * @param {Assignment} assignment
         * @return {Promise}
         */
        reassess({dispatch}, assignment) {
            return new Promise((resolve, reject) => {
                axios.post("rpc", {
                    action: "assignments.reassess",
                    params: {
                        assignment: assignment.id
                    }
                }, {
                    validateStatus: (status => status === 200)
                }).then(() => {
                    dispatch("refresh", assignment).then(() => {
                        resolve();
                    }).catch((error) => {
                        reject(error);
                    });
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Fetches given assignment's data and updates module state.
         *
         * @param {function} commit
         * @param {function} dispatch
         * @param {object} rootGetters
         * @param {object} assignment
         * @return {Promise}
         */
        refresh({commit, rootGetters}, assignment) {
            return axios.get(`/assignments/${assignment.id}`, {
                validateStatus: (status) => {
                    return status === 200;
                }
            }).then((response) => {
                const freshAssignment = Assignment.fromAPI(response.data);
                freshAssignment.textbase = rootGetters["permissions/oneTextbase"](response.data.textbase.id);
                freshAssignment.text = rootGetters["texts/one"](response.data.text.id);
                commit("set", freshAssignment);
            });
        },
        /**
         * Updates given `assignment` through Echoline API.
         *
         * @param {function} commit
         * @param {function} dispatch
         * @param {Assignment} assignment
         * @return {Promise}
         */
        update({commit}, assignment) {
            return axios.put(`/assignments/${assignment.id}`, {
                isViewed: assignment.isViewed,
                isInformational: assignment.isInformational
            }).then((response) => {
                assignment.isViewed = response.data.isViewed;
                assignment.isInformational = response.data.isInformational;
                commit("set", assignment);
            });
        }
    },
    getters: {
        /**
         *
         * @param state
         * @param getters
         * @return {object}
         */
        byText(state, getters) {
            let out = {};

            getters.filtered.forEach((assignment) => {
                if (Object.prototype.hasOwnProperty.call(out, [assignment.text.internalID]) === false) {
                    out[assignment.text.internalID] = [];
                }

                out[assignment.text.internalID].push(assignment);
            });

            return out;
        },
        /**
         * Returns an object where keys are text IDs and values are corresponding array of `Assignment` instances.
         * Assignments will have been previously filtered using new model filters.
         * @param {object} state
         * @param {object} getters
         * @return {string: Assignment[]}
         */
        byTextNG(state, getters) {
            let out = {};

            getters.filteredNG.forEach((assignment) => {
                if (Object.prototype.hasOwnProperty.call(out, [assignment.text.id]) === false) {
                    out[assignment.text.id] = [];
                }

                out[assignment.text.id].push(assignment);
            });

            return out;
        },
        /**
         * Returns all assignments stored in module state, filtered accordingly.
         *
         * @param state
         * @param getters
         * @param rootState
         * @param rootGetters
         * @return {Assignment[]}
         */
        filtered(state, getters, rootState, rootGetters) {
            const filteredTextbasesIDs = rootGetters["permissions/filteredTextbases"].map((textbase) => {
                return textbase.id;
            });

            return Object.values(state.assignments).filter((assignment) => {
                if (!(filteredTextbasesIDs.includes(assignment.textbase.id))) {
                    return false;
                }

                let filter = rootGetters["filters/one"]("watch.status.isViewed");

                if (filter) {
                    return filter.value === assignment.isViewed;
                }

                if (assignment.text.isInformational) {
                    return true;
                }

                filter = rootGetters["filters/one"]("watch.textdates.reassessment");

                if (filter) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    if (assignment.reassessmentDate === null) {
                        return false;
                    }

                    const timestamp = assignment.reassessmentDate.valueOf();

                    if (
                        timestamp < filter.value[0].getTime()
                        || timestamp > filter.value[1].getTime()
                    ) {
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.status.reassessment");

                if (filter) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    if (assignment.reassessmentDate === null) {
                        return !filter.value;
                    }

                    if (filter.value === true) {
                        const now = DateTime.now().set({hour: 0, minute: 0, second: 0});
                        const maxDate = DateTime.fromISO(now).plus({months: 2});
                        return assignment.reassessmentDate < maxDate;
                    }
                }

                if (assignment.stats.nbAssessableRequirements === 0) {
                    return true;
                }

                filter = rootGetters["filters/one"]("watch.status.relevant");

                if (filter) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    let excludedAssessments = 0;

                    filter.value.forEach((value) => {
                        if (value === Assessment.statusUnsure) {
                            excludedAssessments += assignment.stats["relevance.toCheck"];
                        }

                        if (value === Assessment.statusNegative) {
                            excludedAssessments += assignment.stats["relevance.no"];
                        }

                        if (value === Assessment.statusPositive) {
                            excludedAssessments += assignment.stats["relevance.yes"];
                        }

                        if (value === Assessment.statusUnset) {
                            excludedAssessments += assignment.stats["relevance.notAssessed"];
                        }
                    });

                    if (excludedAssessments === assignment.stats.nbAssessableRequirements) {
                        return false;
                    }
                }

                filter = rootGetters["filters/one"]("watch.status.compliant");

                if (filter) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    let excludedAssessments = 0;

                    filter.value.forEach((value) => {
                        if (value === Assessment.statusUnsure) {
                            excludedAssessments += assignment.stats["compliance.toCheck"];
                        }

                        if (value === Assessment.statusNegative) {
                            excludedAssessments += assignment.stats["compliance.no"];
                        }

                        if (value === Assessment.statusPositive) {
                            excludedAssessments += assignment.stats["compliance.yes"];
                        }

                        if (value === Assessment.statusUnset) {
                            excludedAssessments += assignment.stats["compliance.notAssessed"];
                        }
                    });

                    if (excludedAssessments === assignment.stats.nbAssessableRequirements) {
                        return false;
                    }
                }

                return true;
            });
        },
        /**
         * Returns currently stored assignments filtered according to current new model filters.
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {object} rootGetters
         * @return {Assignment[]}
         */
        filteredNG(state, getters, rootState, rootGetters) {
            /**
             * @type {Store}
             */
            const filters = rootGetters["ngFilters/store"];
            const filteredTextbasesIDs = rootGetters["permissions/filteredTextbasesNG"].map((textbase) => {
                return textbase.id;
            });

            return Object.values(state.assignments).filter((assignment) => {
                if (!(filteredTextbasesIDs.includes(assignment.textbase.id))) {
                    return false;
                }

                /* Filtering based on "viewed" status */

                if (
                    filters.assignmentIsViewed.value === "1"
                    && !assignment.isViewed
                ) {
                    return false;
                }

                if (
                    filters.assignmentIsViewed.value === "0"
                    && assignment.isViewed
                ) {
                    return false;
                }

                /*
                    Filtering on **assigned text** "informational" status.
                    All following filters apply only to assignments corresponding to assessable texts.
                 */

                if (assignment.text.isInformational) {
                    return true;
                }

                /* Filtering on reassessment date */

                if (!filters.assignmentReassessmentDateRange.isUnbound()) {
                    if (
                        assignment.isInformational
                        || assignment.reassessmentDate === null
                    ) {
                        return false;
                    }

                    if (!filters.assignmentReassessmentDateRange.isIncluded(assignment.reassessmentDate)) {
                        return false;
                    }
                }

                /*
                    Filtering on number of assessable requirements.
                    All following filters apply to assignments with a positive number of assessable requirements.
                 */

                if (assignment.stats.nbAssessableRequirements === 0) {
                    return true;
                }

                /* Filtering by relevance stats */

                if (!filters.assignmentAssessmentRelevanceStatus.isEmpty) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    let excludedAssessments = [
                        [Assessment.statusUnsure, "relevance.toCheck"],
                        [Assessment.statusNegative, "relevance.no"],
                        [Assessment.statusPositive, "relevance.yes"],
                        [Assessment.statusUnset, "relevance.notAssessed"]
                    ].reduce((accumulator, pair) => {
                        if (filters.assignmentAssessmentRelevanceStatus.isExcluded(pair[0])) {
                            accumulator += assignment.stats[pair[1]];
                        }

                        return accumulator;
                    }, 0);

                    if (excludedAssessments === assignment.stats.nbAssessableRequirements) {
                        return false;
                    }
                }

                /* Filtering by compliance stats */

                if (!filters.assignmentAssessmentComplianceStatus.isEmpty) {
                    if (assignment.isInformational) {
                        return false;
                    }

                    let excludedAssessments = [
                        [Assessment.statusUnsure, "compliance.toCheck"],
                        [Assessment.statusNegative, "compliance.no"],
                        [Assessment.statusPositive, "compliance.yes"],
                        [Assessment.statusUnset, "compliance.notAssessed"]
                    ].reduce((accumulator, pair) => {
                        if (filters.assignmentAssessmentComplianceStatus.isExcluded(pair[0])) {
                            accumulator += assignment.stats[pair[1]];
                        }

                        return accumulator;
                    }, 0);

                    if (excludedAssessments === assignment.stats.nbAssessableRequirements) {
                        return false;
                    }
                }

                return true;
            });
        },
        /**
         * Returns all assignments stored in module state for given text, filtered accordingly.
         *
         * @param state
         * @param getters
         * @returns {function(Text): Assignment[]}
         */
        forText(state, getters) {
            return (text) => {
                if (!(text instanceof Text)) {
                    throw new TypeError("Function returned by VueX 'assignments/forText' getter expects parameter 1 to be an instance of Text");
                }

                return getters.filtered.filter((assignment) => {
                    return assignment.text.internalID === text.internalID;
                }).sort((assignmentA, assignmentB) => {
                    return assignmentA.textbase.name.localeCompare(assignmentB.textbase.name);
                });
            };
        },
        /**
         * Returns all assignments stored in module state for given text, filtered according to new model filters.
         * @param {object} state
         * @param {object} getters
         * @return {function(Text): Assignment[]}
         */
        forTextNG(state, getters) {
            return (text) => {
                if (!(text instanceof Text)) {
                    throw new TypeError(`Function returned by VueX 'assignments/forTextNG' getter expects parameter 1 to be an instance of Text, ${typeof text} given`);
                }

                return getters.filteredNG.filter((assignment) => {
                    return assignment.text.id === text.id;
                }).sort((assignmentA, assignmentB) => {
                    return assignmentA.textbase.name.localeCompare(assignmentB.textbase.name);
                });
            }
        }
    },
    mutations: {
        /**
         * Sets given assignment in module state.
         *
         * @param {object} state
         * @param {Assignment} assignment
         */
        set(state, assignment) {
            if (!(assignment instanceof Assignment)) {
                throw new TypeError(`VueX 'assignments/set' mutation expects payload to be an instance of Assignment, ${typeof assignment} given`);
            }

            state.assignments.hasOwnProperty(assignment.id)
                ? state.assignments[assignment.id] = assignment
                : Vue.set(state.assignments, assignment.id, assignment);

            if (state.assignmentsByText.hasOwnProperty(assignment.text.internalID) === false) {
                Vue.set(state.assignmentsByText, assignment.text.internalID, []);
            }

            if (state.assignmentsByText[assignment.text.internalID].includes(assignment.id) === false) {
                state.assignmentsByText[assignment.text.internalID].push(assignment.id);
            }
        }
    },
    namespaced: true,
    state: {
        assignments: {},
        assignmentsByText: {},
    }
}
